import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "../ui/button";
import { ArrowRight, Quote, Database, User } from "lucide-react";
import { ScrollReveal } from "../ui/scroll-reveal";
import { EventCategory } from "../../lib/analytics";
import { safeTrackEvent } from "../../lib/analytics-utils";
import { getTestimonialForPage } from "../../lib/testimonials";

export function StartJourneyCta() {
  const location = useLocation();
  const testimonial = getTestimonialForPage('home');
  
  const handleCtaClick = (action: string, destination: string) => {
    safeTrackEvent({
      category: EventCategory.Engagement,
      label: `engagement_home_${action.toLowerCase().replace(/ /g, '_')}`,
      context: {
        sourcePage: location.pathname,
        destinationPage: destination,
        section: "prominent_cta",
        action: action
      }
    });
  };
  
  const handleViewMoreClick = () => {
    safeTrackEvent({
      category: EventCategory.Navigation,
      action: 'click',
      label: `nav_home_success_stories`,
      value: 1,
      context: {
        sourcePage: location.pathname
      }
    });
  };
  
  return (
    <section className="pt-12 pb-24">
      <div className="container">
        <ScrollReveal>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Success Stories Box */}
            <div className="rounded-xl bg-gradient-to-r from-primary/5 to-transparent p-8 border border-primary/10 shadow-sm flex flex-col h-full">
              <div className="flex-grow space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold">Success Stories</h2>
                  <Quote className="w-8 h-8 text-primary/20" strokeWidth={1} />
                </div>
                {testimonial && (
                  <>
                    <p 
                      className="text-base italic text-muted-foreground"
                      dangerouslySetInnerHTML={{ __html: `"${testimonial.quote}"` }}
                    />
                    <div className="flex items-center gap-3 mt-2">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        {testimonial.anonymous ? (
                          <User className="w-5 h-5 text-primary" strokeWidth={1.5} />
                        ) : (
                          <span className="text-primary font-semibold">
                            {testimonial.name && testimonial.name.trim() 
                              ? testimonial.name.split(' ')[0][0] + (testimonial.name.split(' ')[1]?.[0] || '')
                              : testimonial.role.split(' ').map(word => word[0]).join('')}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium">
                          {testimonial.anonymous ? testimonial.role : testimonial.name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {testimonial.anonymous 
                            ? testimonial.company
                            : (testimonial.role ? `${testimonial.role}, ` : '') + testimonial.company
                          }
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="pt-4 flex justify-end">
                <Button 
                  variant="link" 
                  asChild 
                  size="sm"
                  onClick={handleViewMoreClick}
                >
                  <Link to="/success-stories">View all success stories →</Link>
                </Button>
              </div>
            </div>
            
            {/* Our Approach Box */}
            <div className="rounded-xl bg-gradient-to-r from-primary/10 to-transparent p-8 border border-primary/10 shadow-sm flex flex-col h-full">
              <div className="flex-grow space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                    <Database className="w-6 h-6 text-primary-foreground" strokeWidth={2} />
                  </div>
                  <span className="text-xl font-bold text-primary">Our Approach</span>
                </div>
                <p className="text-muted-foreground">
                  Not another AI platform. Deep consulting expertise in knowledge engineering.
                </p>
                <div className="space-y-3">
                  <p><span className="font-medium">Expert consultants</span> <span className="text-muted-foreground">who capture what makes your experienced people effective.</span></p>
                  <p><span className="font-medium">Knowledge Architecture</span> <span className="text-muted-foreground">that structures knowledge into systems AI can use.</span></p>
                  <p><span className="font-medium">Flexible engagement</span> <span className="text-muted-foreground">your way. Your AI or ours. Kickstarter or long-term.</span></p>
                </div>
              </div>
              <div className="pt-4 flex justify-end">
                <Button
                  variant="link"
                  asChild
                  size="sm"
                  onClick={() => handleCtaClick("Our Approach", "/our-approach")}
                >
                  <Link to="/our-approach">How we work →</Link>
                </Button>
              </div>
            </div>
            
            {/* Main CTA Box */}
            <div className="rounded-xl bg-gradient-to-r from-primary/10 to-transparent p-8 border border-primary/10 shadow-sm flex flex-col h-full">
              <div className="flex-grow flex flex-col justify-center">
                <div className="text-center space-y-4">
                  <h2 className="text-xl md:text-2xl font-bold">Ready to Build Sustainable<br className="hidden sm:inline" /> AI Capability?</h2>
                  <p className="text-base text-muted-foreground">
                    Let's discuss how our unique approach turns AI investments into lasting organisational capability.
                  </p>
                </div>
              </div>
              <div className="pt-6">
                <Button
                  size="lg"
                  className="text-base px-10 py-5 w-full"
                  onClick={() => handleCtaClick("Schedule a Consultation", "/contact")}
                  asChild
                >
                  <Link to="/contact" className="flex items-center justify-center gap-2">
                    Schedule a Consultation <ArrowRight className="ml-1 w-5 h-5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
